import { useContext, useState } from "react";
import { Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { ToolContext } from "../context/ToolContext";
import { FormContext } from "../context/FormContext";
import { groupBy } from "../utils/groupBy";
import { getLocalStorageValue, setLocalStorageValue } from "../utils/useLocalStorage";
import {
  ClientSettingsFilterResults,
  ClientSettingsFilterResultsInfo,
} from "../model/reference/settingsTypes";
import EATooltip from "./EATooltip";
import IconRadioGrp from "./IconRadioGrp";
import WarningModal from "./WarningModal";
import { SimpleFilterResultsInfo } from "./SimpleFilterResultsInfo";

interface FaultFilterHeaderProps {
  faults: Fault[];
  getOverallSeason: () => string;
}

interface Fault {
  season: string;
  name: string;
  nonSeasonalValue: string;
  value: string;
  constraintCount: number;
}

interface RadioItem {
  season: string;
}

const FaultFilterHeader = ({ faults, getOverallSeason }: FaultFilterHeaderProps) => {
  const { toolState } = useContext(ToolContext);
  const { formState, dispatch } = useContext(FormContext);
  const [customSettings, setCustomSettings] = useState<SimpleFilterResultsInfo[] | null>(null);
  const [notifySave, setNotifySave] = useState<boolean>(false);
  const [showOverwriteWarning, setShowOverwriteWarning] = useState(false);

  const getCustomSettings = (): SimpleFilterResultsInfo[] => {
    if (!customSettings) {
      const _filterSettings = getLocalStorageValue("resultSettings", []);
      setCustomSettings(_filterSettings);
      return _filterSettings;
    }
    return customSettings;
  };

  let filterResults: ClientSettingsFilterResults[] = [];

  if (formState && formState.clientSettings && formState.clientSettings.filterResults) {
    filterResults = [...formState.clientSettings.filterResults];
  }

  const clientDefaultInfos: SimpleFilterResultsInfo[] =
    formState?.originalClientSettings?.filterResults?.flatMap((r: ClientSettingsFilterResults) =>
      r.info.map((i) => SimpleFilterResultsInfo.getNew(i)),
    ) ?? [];

  const enableAllWarningsButton = (): boolean => {
    return (
      toolState.showResults &&
      faults.some((p) => p.constraintCount > 0) &&
      !resultsAreShown(getWarningInfos())
    );
  };

  const enableClearButton = (): boolean => {
    return !resultsAreShown([]);
  };

  const enableDefaultsButton = (): boolean => {
    return !resultsAreShown(clientDefaultInfos);
  };

  const enableLoadCustomButton = (): boolean => {
    const infos = getCustomSettings();
    return !!infos.length && !resultsAreShown(infos);
  };

  const enableSaveCustomButton = (): boolean => {
    return isAtLeastOneItemTicked() && !resultsAreShown(getCustomSettings());
  };

  const isAtLeastOneItemTicked = (): boolean => {
    return filterResults.some((filterResult) => filterResult.info.some((info) => info.isSet));
  };

  const hasSeasons = (): boolean => {
    return filterResults.some((filterResult) =>
      filterResult.info.some((info) => info.isSet && info.season),
    );
  };

  const getWarningInfos = (): SimpleFilterResultsInfo[] => {
    const activeFaults = faults.filter((p) => p.constraintCount > 0);
    const faultGroups = groupBy(activeFaults, "name");
    const infos: SimpleFilterResultsInfo[] = [];

    clientDefaultInfos.forEach((info) => {
      const faultGroup: Fault[] = faultGroups[info.name];
      if (faultGroup) {
        if (info.hasOptions) {
          const valueToSelect = faultGroup.some((f) => f.nonSeasonalValue === info.value)
            ? info.value
            : faultGroup[0].nonSeasonalValue;

          const faultSeason = getFaultSeason(
            faultGroup.filter((g) => g.nonSeasonalValue === valueToSelect),
          );
          const newInfo = new SimpleFilterResultsInfo(
            true,
            info.name,
            faultSeason,
            valueToSelect,
            true,
          );
          infos.push(newInfo);
        } else {
          const faultSeason = getFaultSeason(faultGroups[info.name]);
          const newInfo = new SimpleFilterResultsInfo(
            true,
            info.name,
            faultSeason,
            info.value,
            false,
          );
          infos.push(newInfo);
        }
      }
    });
    return infos;
  };

  const getFaultSeason = (faults: Fault[]): string => {
    const seasons = faults.map((f) => f.season);
    return seasons.sort()[0];
  };

  const showAllWarnings = (): void => {
    dispatchInfos(getWarningInfos());
  };

  const loadCustom = () => {
    dispatchInfos(getCustomSettings());
  };

  const saveCustomClicked = () => {
    setShowOverwriteWarning(true);
  };

  const saveCustom = (okClicked: boolean) => {
    setShowOverwriteWarning(false);
    if (!okClicked) {
      return;
    }

    const infos: SimpleFilterResultsInfo[] = filterResults
      .flatMap((r: ClientSettingsFilterResults) =>
        r.info.map((i) => SimpleFilterResultsInfo.getNew(i)),
      )
      .filter((i) => i.isSet);
    if (infos?.length) {
      setLocalStorageValue("resultSettings", infos);
      setCustomSettings(infos);
    }
    setNotifySave(true);
    setTimeout(() => {
      setNotifySave(false);
    }, 1500);
  };

  const clearSettings = () => dispatchInfos([]);

  const showDefaults = () => {
    if (!resultsAreShown(clientDefaultInfos)) {
      dispatchInfos(clientDefaultInfos);
    }
  };

  const updateOverallSeason = (season: string): void => {
    const infos: SimpleFilterResultsInfo[] = [];

    filterResults.forEach((filterResult) => {
      filterResult.info
        .filter((info) => info.isSet)
        .forEach((info) => {
          const newInfo = SimpleFilterResultsInfo.getNew(info);
          if (info.season) {
            newInfo.season = season;
          }
          infos.push(newInfo);
        });
    });

    dispatchInfos(infos);
  };

  const resultsAreShown = (infos: SimpleFilterResultsInfo[]): boolean => {
    const _formInfos: ClientSettingsFilterResultsInfo[] = filterResults.flatMap(
      (r: ClientSettingsFilterResults) => r.info,
    );

    const result = _formInfos.every((formInfo) => {
      let newInfo: SimpleFilterResultsInfo | undefined;
      if (formInfo.options?.length) {
        newInfo = infos.find((i) => i.name === formInfo.name);
        if (newInfo) {
          if (newInfo.value !== formInfo.value) {
            return false;
          }
        }
      } else {
        newInfo = infos.find((i) => i.name === formInfo.name && i.value === formInfo.value);
      }
      if (formInfo.isSet) {
        return newInfo?.isSet && (newInfo?.season ?? "") === (formInfo.season ?? "");
      } else {
        return !(newInfo?.isSet ?? false);
      }
    });
    return result;
  };

  const dispatchInfos = (infos: SimpleFilterResultsInfo[]): void => {
    const _formState = { ...formState };
    const _formInfos: ClientSettingsFilterResultsInfo[] =
      _formState.clientSettings.filterResults.flatMap((r: ClientSettingsFilterResults) => r.info);

    _formInfos.forEach((formInfo) => {
      let newInfo: SimpleFilterResultsInfo | undefined;
      if (formInfo.options?.length) {
        newInfo = infos.find((i) => i.name === formInfo.name);
        formInfo.value = newInfo?.value ?? "";
      } else {
        newInfo = infos.find((i) => i.name === formInfo.name && i.value === formInfo.value);
      }
      formInfo.isSet = newInfo?.isSet ?? false;
      if (newInfo?.season) {
        formInfo.season = newInfo.season;
      }
    });

    dispatch({
      form: "clientSettings",
      obj: _formState.clientSettings,
      type: "REPLACE_STATE",
    });
  };

  return (
    <>
      {showOverwriteWarning && (
        <WarningModal
          {...({
            item: true,
            action: saveCustom,
            msg: "Overwrite your previously saved settings - are you sure?",
            yesLabel: "OK",
            dismissLabel: "Cancel",
          } as any)}
        />
      )}
      <div className="d-flex flex-row mb-3" style={{ marginRight: "4.35rem" }}>
        <EATooltip
          text="Load your custom result settings"
          target="show-custom-button"
          placement="top"
        />
        <Button
          name="show-custom-button"
          id="show-custom-button"
          size="sm"
          color="primary"
          onClick={loadCustom}
          disabled={!enableLoadCustomButton()}
        >
          Load
        </Button>
        {!showOverwriteWarning && (
          <EATooltip
            text="Save these result settings as your custom settings"
            target="save-custom-button"
            placement="top"
          />
        )}
        <Button
          name="save-custom-button"
          id="save-custom-button"
          className="ml-3 mr-auto"
          size="sm"
          color={notifySave ? "success" : "primary"}
          onClick={saveCustomClicked}
          disabled={!enableSaveCustomButton()}
        >
          <div style={{ position: "relative", display: "inline-block" }}>
            <span style={{ visibility: notifySave ? "hidden" : "visible" }}>Save</span>
            <FontAwesomeIcon
              style={{
                visibility: notifySave ? "visible" : "hidden",
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
              icon={faCheck}
            />
          </div>
        </Button>
        <EATooltip text="Clear all result settings" target="clear-button" placement="top" />
        <Button
          name="clear-button"
          id="clear-button"
          size="sm"
          color="primary"
          onClick={clearSettings}
          disabled={!enableClearButton()}
        >
          Clear
        </Button>
        <EATooltip
          text="Load the default result settings"
          target="show-defaults-button"
          placement="top"
        />
        <Button
          name="show-defaults-button"
          id="show-defaults-button"
          className="ml-3"
          size="sm"
          color="primary"
          onClick={showDefaults}
          disabled={!enableDefaultsButton()}
        >
          Defaults
        </Button>
      </div>
      <div className="d-flex flex-row align-items-center" style={{ marginRight: "4.35rem" }}>
        <EATooltip
          text="Change result settings to show all over-limit values."
          target="show-warnings-button"
          placement="top"
        />
        <Button
          size="sm"
          color="danger"
          name="show-warnings-button"
          id="show-warnings-button"
          onClick={showAllWarnings}
          disabled={!enableAllWarningsButton()}
        >
          Display All Over-Limit Values
        </Button>
        <span style={{ cursor: "pointer" }} className="ml-auto">
          {hasSeasons() && (
            <IconRadioGrp
              array={[
                {
                  name: "summer",
                  color: "warning",
                  icon: "sun",
                },
                {
                  name: "winter",
                  color: "info",
                  icon: "snowflake",
                },
                {
                  name: "All",
                  color: "light",
                  icon: "all",
                },
              ]}
              value={getOverallSeason()}
              setValue={(e: RadioItem) => updateOverallSeason(e.season)}
            />
          )}
        </span>
      </div>
    </>
  );
};
export default FaultFilterHeader;
